import React, { useMemo } from 'react';
import CgSearchFilter from 'modules/Common/ui/CgSearchFilter';
import { CgFilterSelect } from 'modules/Common/ui/CgFilterDropdown';
import CgRadixButton from 'modules/Common/ui/CgRadixButton';
import { faRotateRight } from '@fortawesome/free-solid-svg-icons';
import { instanceLabel } from '../hooks/useAdminInstances';

/**
 * There is NO free-text search box. The API takes four independent per-field
 * filters and no combined `search` (phase 2 §4), so the UI mirrors that: one input
 * per identifier, ANDed. A single box would have to guess which field a string
 * belongs to, and guessing wrong on `EORI` vs `clerkOrganisationId` returns an
 * empty list that looks like "no such organisation".
 *
 * Fully CONTROLLED over useCgTable's draft filters — `formData`, `onFormChange`,
 * `onReset` are the three values the hook returns. Nothing here holds filter state
 * of its own, which is what keeps the URL, the query key and the inputs from ever
 * disagreeing.
 */

const TEXT_FILTERS = [
  { name: 'name', label: 'Organization name', type: 'text', placeholder: 'Acme Steel' },
  { name: 'contactEmail', label: 'Contact email', type: 'text', placeholder: 'ops@acme.example' },
  { name: 'EORI', label: 'EORI', type: 'text', placeholder: 'ES123456789' },
  {
    name: 'clerkOrganisationId',
    label: 'Clerk org ID',
    type: 'text',
    placeholder: 'org_2abc…',
  },
];

const TYPE_OPTIONS = [
  { value: 'declarant', label: 'Declarant' },
  { value: 'operator', label: 'Operator' },
  { value: 'consultant', label: 'Consultant' },
  { value: 'verifier', label: 'Verifier' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'trial', label: 'Trial' },
  { value: 'expired', label: 'Expired' },
  { value: 'none', label: 'No plan' },
  { value: 'n/a', label: 'N/A' },
];

export default function OrganizationFilters({
  form,
  onFormChange,
  onReset,
  loading,
  onRefresh,
  instances,
  isMultiInstance,
}) {
  const brandOptions = useMemo(
    () =>
      instances.map((instance) => ({
        value: instance.clerkInstance,
        label: instanceLabel(instance),
      })),
    [instances],
  );

  // One-value changes go through the same payload path as the popover's Apply, so
  // useCgTable commits them and resets to page 1 identically. Spreading `form`
  // keeps the other filters — a partial payload would reset them to defaults.
  const setOne = (key) => (value) => onFormChange({ ...form, [key]: value ?? '' });

  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <CgSearchFilter
        filters={TEXT_FILTERS}
        formData={form}
        onFormChange={onFormChange}
        onReset={onReset}
        tableTitle="Organizations"
        tableKey="admin-organizations"
      />

      <div className="w-44">
        <CgFilterSelect
          label={null}
          options={TYPE_OPTIONS}
          value={form.type || null}
          onChange={setOne('type')}
          placeholder="All types"
        />
      </div>

      <div className="w-44">
        <CgFilterSelect
          label={null}
          options={STATUS_OPTIONS}
          value={form.planStatus || null}
          onChange={setOne('planStatus')}
          placeholder="All statuses"
        />
      </div>

      {/*
        Brand appears only on a multi-instance deployment. Its options come from
        useAdminInstances(), never from the clerkInstance values in the response:
        those show only brands that already HAVE organisations — hiding precisely
        the brand being onboarded — and they shift as you page. A value outside the
        caller's managed set is a 400, not an empty list, so the control must never
        be able to offer one.
      */}
      {isMultiInstance ? (
        <div
          className="w-52"
          data-testid="admin-brand-filter"
        >
          <CgFilterSelect
            label={null}
            options={brandOptions}
            value={form.clerkInstance || null}
            onChange={setOne('clerkInstance')}
            placeholder="All brands"
          />
        </div>
      ) : null}

      <CgRadixButton
        variant="secondary"
        icon={faRotateRight}
        label="Refresh"
        loading={loading}
        onClick={onRefresh}
        className="ml-auto"
        data-testid="admin-refresh"
      />
    </div>
  );
}
